'use client'

import type { CarouselTitle, CarouselVariants } from '@fukku/carousel/types'
import { ClientCrossSelling } from 'cross-selling/components/shared/CrossSelling/client/ClientCrossSelling'
import { useLastViewed } from 'cross-selling/contexts/LastViewedProvider/LastViewedProvider'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver/useIntersectionObserver'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useProductsInfo } from 'product/services/productInfo/client/useProductsInfo/useProductsInfo'
import type { ProductImageType } from 'product/types'
import type { ProductLocations } from 'product/types/ProductLocations'
import { type ReactNode, useEffect, useRef, useState } from 'react'

const imageSortCriteria: ProductImageType[] = ['B']

export interface LastViewedCrossSellingProps {
	readonly dataTestId: string
	readonly title: string
	readonly location: ProductLocations
	readonly showDiscount?: boolean
	readonly variant?: CarouselVariants
}

export function LastViewedCrossSelling({
	dataTestId,
	title,
	location,
	showDiscount,
	variant,
}: LastViewedCrossSellingProps): ReactNode {
	const [shouldFetch, setShouldFetch] = useState(false)
	const { t } = useLabels()
	const {
		country: { countryISO, languageISO },
	} = useMasterData()
	const { lastViewedProducts } = useLastViewed()
	const crossSellingRef = useRef<HTMLDivElement | null>(null)
	const crossSellingEntry = useIntersectionObserver(crossSellingRef, {
		threshold: [0, 1],
		rootMargin: '0px 0px 150px 0px',
		freezeOnceVisible: true,
	})

	const lastViewedProductsIds = lastViewedProducts?.map(
		(product) => product.productId
	)

	const { data: lastViewedProductsData } = useProductsInfo({
		country: countryISO,
		language: languageISO,
		productIds: lastViewedProductsIds,
		enabled: shouldFetch,
	})

	useEffect(() => {
		if (crossSellingEntry?.isIntersecting) {
			setShouldFetch(true)
		}
	}, [crossSellingEntry?.isIntersecting])

	const titleProps: CarouselTitle = {
		content: t(title),
	}

	if (shouldFetch && lastViewedProductsData.length === 0) {
		return null
	}

	return (
		<div ref={crossSellingRef}>
			{lastViewedProducts &&
				lastViewedProducts.length > 0 &&
				lastViewedProductsData.length > 0 && (
					<ClientCrossSelling
						crossSellingItems={lastViewedProducts}
						productsData={lastViewedProductsData}
						data-testid={dataTestId}
						imageSortCriteria={imageSortCriteria}
						title={titleProps}
						location={location}
						productListId={location}
						hasFavoriteButton
						showDiscount={showDiscount}
						variant={variant}
					/>
				)}
		</div>
	)
}
